import { deleteObject, getBytes, getMetadata, listAll, ref, uploadBytes, type FirebaseStorage, type StorageReference } from 'firebase/storage';
import type { BookingDocument } from '../types';
import type { ItineraryOriginal } from './itineraryFiles';
import { BOOKING_DOC_MAX_BYTES, encodeBookingDocument } from '../../functions/src/bookingDocumentCompression.mjs';

/**
 * Original booking documents — the PDF, screenshot or email a booking came
 * from — kept with the trip so travellers can open the e-ticket from the
 * itinerary. Private to the trip's members (storage.rules).
 *
 * Uploaded in the app: stored straight into the trip at save time. Forwarded
 * by email: the server keeps them under the sender until they choose a trip
 * in the review, then they're copied into it (a client can't move Storage
 * objects, and the sender's private copy isn't readable by the trip).
 */

export { BOOKING_DOC_MAX_BYTES };

/** The formatted email and its raw download are one attachment, within one import only. */
export function bookingDocumentEntries(docs: BookingDocument[]) {
  const folder = (doc: BookingDocument) => doc.path.slice(0, doc.path.lastIndexOf('/'));
  const body = (doc: BookingDocument) => doc.kind === 'email-html' || doc.name === 'email.txt';
  const raw = (doc: BookingDocument) => doc.name === 'original.eml' && doc.contentType === 'message/rfc822';
  return docs.filter(doc => !raw(doc) || !docs.some(other => body(other) && folder(other) === folder(doc)))
    .map(document => ({ document, download: body(document)
      ? docs.find(other => raw(other) && folder(other) === folder(document)) ?? document : document }));
}

/** A name safe as one Storage path segment, keeping the extension readable. */
export const safeDocName = (name: string) =>
  (name || 'document').replace(/[/\\#?[\]*]/g, '_').replace(/\s+/g, ' ').trim().slice(0, 120) || 'document';

export async function storeBookingDocument(
  storage: FirebaseStorage, tripId: string, importKey: string, file: Blob, name: string, contentType: string,
): Promise<BookingDocument | null> {
  if (file.size >= BOOKING_DOC_MAX_BYTES) return null;
  const fileName = safeDocName(name);
  const path = `trips/${tripId}/bookingDocs/${importKey}/${fileName}`;
  const object = ref(storage, path);
  const encoded = await encodeBookingDocument(new Uint8Array(await file.arrayBuffer()));
  try {
    await uploadBytes(object, new Uint8Array(encoded.bytes), { contentType, cacheControl: 'private, max-age=0',
      ...(encoded.contentEncoding ? { contentEncoding: encoded.contentEncoding } : {}),
      ...(!/^(application\/pdf|image\/)/.test(contentType) ? { contentDisposition: `attachment; filename="${fileName.replace(/["\r\n]/g, '')}"` } : {}),
    });
  } catch (error) {
    // A reopened email review may reuse this immutable path after some items
    // saved. Only link the old object if its bytes are exactly the same.
    const existing = await getBytes(object, BOOKING_DOC_MAX_BYTES).catch(() => null);
    const incoming = new Uint8Array(await file.arrayBuffer());
    if (!existing || existing.byteLength !== incoming.length ||
      !new Uint8Array(existing).every((byte, i) => byte === incoming[i])) throw error;
  }
  return { path, name: fileName, contentType };
}

/** Every file gets a content-addressed folder: retries and duplicate names are safe. */
export async function storeItineraryOriginals(
  storage: FirebaseStorage, tripId: string, originals: ItineraryOriginal[], files: Map<string, File>,
  checkpoint: () => void,
): Promise<BookingDocument[]> {
  const docs: BookingDocument[] = [];
  for (const original of originals) {
    const key = `itinerary-${original.sha256}`;
    const path = `trips/${tripId}/bookingDocs/${key}/${safeDocName(original.name)}`;
    // Do not trust a restored draft to redirect documents into another scope.
    if (original.document?.path !== path) delete original.document;
    if (!original.document) {
      const file = files.get(original.sha256);
      if (!file) throw new Error(`Reselect ${original.name} to keep the original. Your reviewed itinerary is still here.`);
      const stored = await storeBookingDocument(storage, tripId, key, file, original.name, original.contentType);
      if (!stored) throw new Error('Each attachment must be smaller than 15 MiB.');
      original.document = stored;
      checkpoint();
    }
    docs.push(original.document);
  }
  return docs;
}

/** Route-only imports have no booking card; originals remain reachable from Itinerary. */
export async function listItineraryOriginals(storage: FirebaseStorage, tripId: string): Promise<BookingDocument[]> {
  const listing = await listAll(ref(storage, `trips/${tripId}/bookingDocs`));
  const folders = listing.prefixes.filter(folder => /^itinerary-[a-f0-9]{64}$/.test(folder.name));
  const documents = await Promise.all(folders.map(async folder => {
    const files = await listAll(folder);
    return Promise.all(files.items.map(async file => {
      const metadata = await getMetadata(file);
      return { path: file.fullPath, name: file.name, contentType: metadata.contentType || 'application/octet-stream' };
    }));
  }));
  return documents.flat();
}

/** Copy a forwarded import's private documents into the trip. */
export async function copyImportDocuments(
  storage: FirebaseStorage, tripId: string, importKey: string, docs: BookingDocument[],
): Promise<BookingDocument[]> {
  const out: BookingDocument[] = [];
  for (const d of docs) {
    try {
      const bytes = await getBytes(ref(storage, d.path), BOOKING_DOC_MAX_BYTES);
      const stored = await storeBookingDocument(storage, tripId, importKey, new Blob([bytes], { type: d.contentType }), d.name, d.contentType);
      if (stored) out.push({ ...stored, ...(d.kind ? { kind: d.kind } : {}) });
    } catch { /* Keep the originals that did copy; the review reports missing ones. */ }
  }
  return out;
}

/** Delete everything under a prefix, one folder level of nesting deep (importKey/name). */
export async function deleteDocumentsUnder(storage: FirebaseStorage, prefix: string): Promise<void> {
  const walk = async (folder: StorageReference): Promise<void> => {
    const listing = await listAll(folder);
    await Promise.all([...listing.items.map(item => deleteObject(item)), ...listing.prefixes.map(walk)]);
  };
  await walk(ref(storage, prefix));
}
